import { useEffect, useState } from 'react';
import { institutionApi } from '../../api/institutionsApi';
import { marketsApi } from '../../api/marketsApi';
import { referenceApi } from '../../api/reference';
import { borderCrossingsApi } from '../../api/borderCrossingsApi';
import { vetCertificatesApi } from '../../api/vetCertificatesApi';
import { speciesApi } from '../../api/speciesLivestockRegistryApi';
import { BORDER_POINT_OPTIONS } from './marketTradOptions';

function extractItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  return [];
}

function normalizeOption(item) {
  if (item === null || item === undefined) return null;

  if (typeof item === 'string' || typeof item === 'number') {
    const text = String(item).trim();
    return text ? { value: text, label: text } : null;
  }

  const value = String(
    item.value ??
    item.uuid ??
    item.id ??
    item.code ??
    item.species_code ??
    item.market_uuid ??
    item.institution_uuid ??
    item.market_name ??
    item.marketName ??
    item.common_name_nigeria ??
    item.name ??
    item.label ??
    ''
  ).trim();

  if (!value) return null;

  const label = String(
    item.label ??
    item.name ??
    item.title ??
    item.common_name_nigeria ??
    item.market_name ??
    item.marketName ??
    item.species_code ??
    value
  ).trim() || value;

  return { value, label, raw: item };
}

function normalizeInstitutionOption(item) {
  if (item === null || item === undefined) return null;

  if (typeof item === 'string' || typeof item === 'number') {
    const text = String(item).trim();
    return text ? { value: text, label: text } : null;
  }

  const raw = item.raw ?? item;
  const value = String(
    raw.uuid ??
    raw.id ??
    raw.institution_uuid ??
    raw.institution_id ??
    item.uuid ??
    item.id ??
    item.institution_uuid ??
    item.institution_id ??
    item.value ??
    item.code ??
    item.label ??
    item.name ??
    ''
  ).trim();

  if (!value) return null;

  const label = String(
    raw.name ??
    raw.label ??
    item.label ??
    item.name ??
    item.title ??
    value
  ).trim() || value;

  return { value, label, raw };
}


function normalizeMarketOption(market) {
  if (!market) return null;

  const value = String(market.uuid ?? market.id ?? '').trim();
  if (!value) return null;

  const baseName = String(market.market_name ?? market.marketName ?? market.name ?? value).trim() || value;
  const location = [market.state, market.lga].filter(Boolean).join(' / ');

  return {
    value,
    label: location ? `${baseName} - ${location}` : baseName,
    raw: market,
  };
}

function normalizeVetCertificateOption(item) {
  if (item === null || item === undefined) return null;

  if (typeof item === 'string' || typeof item === 'number') {
    const text = String(item).trim();
    return text ? { value: text, label: text, raw: item } : null;
  }

  const value = String(
    item.value ??
    item.uuid ??
    item.id ??
    item.certificate_number ??
    item.certificateNumber ??
    item.number ??
    item.code ??
    ''
  ).trim();

  if (!value) return null;

  const species = String(
    item.species_label ??
    item.speciesLabel ??
    item.species_name ??
    item.speciesName ??
    ''
  ).trim();

  const state = String(item.state ?? '').trim();
  const issuedAt = String(item.issued_at ?? item.issuedAt ?? '').trim();
  const labelBase = String(
    item.label ??
    item.certificate_number ??
    item.certificateNumber ??
    value
  ).trim() || value;

  const extras = [species, state, issuedAt].filter(Boolean);

  return {
    value,
    label: extras.length ? `${labelBase}${species ? ` · ${species}` : ''}${state ? ` · ${state}` : ''}${issuedAt ? ` · ${issuedAt}` : ''}` : labelBase,
    raw: item,
  };
}

function normalizeBorderPointOption(item) {
  if (item === null || item === undefined) return null;

  if (typeof item === 'string' || typeof item === 'number') {
    const text = String(item).trim();
    return text ? { value: text, label: text, raw: item } : null;
  }

  const raw = item.raw ?? item;
  const value = String(
    item.value ??
    raw.value ??
    raw.border_point_id ??
    raw.borderPointId ??
    raw.crossing_point ??
    raw.crossingPoint ??
    raw.uuid ??
    raw.id ??
    raw.code ??
    raw.label ??
    raw.name ??
    ''
  ).trim();

  if (!value) return null;

  const label = String(
    item.label ??
    raw.label ??
    raw.border_point_label ??
    raw.borderPointLabel ??
    raw.crossing_point ??
    raw.crossingPoint ??
    raw.name ??
    value
  ).trim() || value;

  return { value, label, raw };
}

function mergeUniqueOptions(primary = [], fallback = []) {
  const seen = new Map();

  [...primary, ...fallback]
    .map((option) => option && typeof option === 'object' ? option : normalizeBorderPointOption(option))
    .filter(Boolean)
    .forEach((option) => {
      const key = String(option.value ?? '').trim();
      if (!key || seen.has(key)) return;
      seen.set(key, option);
    });

  return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
}

export function useMarketPriceReferenceOptions() {
  const [marketOptions, setMarketOptions] = useState([]);
  const [speciesOptions, setSpeciesOptions] = useState([]);
  const [dataSourceOptions, setDataSourceOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const [marketPayload, speciesPayload, dataSourcePayload] = await Promise.all([
          marketsApi.listAll({ per_page: 500 }),
          speciesApi.listAll({ per_page: 200 }),
          referenceApi.dataSources(),
        ]);

        if (!active) return;

        setMarketOptions(
          extractItems(marketPayload).map(normalizeMarketOption).filter(Boolean)
        );
        setSpeciesOptions(
          extractItems(speciesPayload).map(normalizeOption).filter(Boolean)
        );
        setDataSourceOptions(
          extractItems(dataSourcePayload).map(normalizeOption).filter(Boolean)
        );
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load market price references.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, []);

  return {
    marketOptions,
    speciesOptions,
    dataSourceOptions,
    loading,
    error,
  };
}

export function useBorderRegistryReferenceOptions() {
  const [speciesOptions, setSpeciesOptions] = useState([]);
  const [dataSourceOptions, setDataSourceOptions] = useState([]);
  const [borderPointOptions, setBorderPointOptions] = useState(BORDER_POINT_OPTIONS);
  const [vetCertificateOptions, setVetCertificateOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        // Load species and data sources — these are required for the form
        const [speciesPayload, dataSourcePayload] = await Promise.all([
          speciesApi.listAll({ per_page: 200 }),
          referenceApi.dataSources(),
        ]);

        if (!active) return;

        setSpeciesOptions(
          extractItems(speciesPayload).map(normalizeOption).filter(Boolean)
        );
        setDataSourceOptions(
          extractItems(dataSourcePayload).map(normalizeOption).filter(Boolean)
        );
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load border registry references.');
      } finally {
        if (active) setLoading(false);
      }

      borderCrossingsApi.listAll({ per_page: 500 })
        .then((borderPayload) => {
          if (!active) return;
          const derived = extractItems(borderPayload).map(normalizeBorderPointOption).filter(Boolean);
          setBorderPointOptions(mergeUniqueOptions(derived, BORDER_POINT_OPTIONS));
        })
        .catch(() => {
          if (!active) return;
          setBorderPointOptions(BORDER_POINT_OPTIONS);
        });

      // Load vet certs separately — optional field, failure must not block the form
      vetCertificatesApi.listAll({ per_page: 500 })
        .then((vetPayload) => {
          if (!active) return;
          setVetCertificateOptions(extractItems(vetPayload).map(normalizeVetCertificateOption).filter(Boolean));
        })
        .catch(() => {}); // silently fail — vet cert link is optional
    };

    load();

    return () => {
      active = false;
    };
  }, []);

  return {
    speciesOptions,
    dataSourceOptions,
    borderPointOptions,
    vetCertificateOptions,
    loading,
    error,
  };
}

export function useBorderVetCreateOptions() {
  const {
    marketOptions,
    speciesOptions,
    dataSourceOptions,
    loading: loadingBase,
    error: baseError,
  } = useMarketPriceReferenceOptions();

  const [institutionOptions, setInstitutionOptions] = useState([]);
  const [loadingInstitutions, setLoadingInstitutions] = useState(true);
  const [institutionError, setInstitutionError] = useState('');

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoadingInstitutions(true);
      setInstitutionError('');

      try {
        // Use /v1/institutions — the same endpoint Workforce uses — so objects
        // have a real `uuid` field that the backend accepts as institution_uuid.
        const items = await institutionApi.list({ per_page: 200 });
        if (!active) return;

        setInstitutionOptions(
          items.map(normalizeInstitutionOption).filter(Boolean)
        );
      } catch (err) {
        if (!active) return;
        setInstitutionError(
          err?.response?.data?.message ?? err?.message ?? 'Failed to load institution references.'
        );
      } finally {
        if (active) setLoadingInstitutions(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, []);

  return {
    marketOptions,
    speciesOptions,
    dataSourceOptions,
    institutionOptions,
    loading: loadingBase || loadingInstitutions,
    error: baseError || institutionError,
  };
}
